import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode, VoidFunctionComponent } from 'react';

import { getSubscription } from '../../../shared/subscriptions';
import { window } from '../../globals';
import { useAsyncStatus } from '../../hooks';
import * as api from '../../redux/api';
import Accordion from '../generic/Accordion';
import { ButtonGroup, InlineLoaderButton } from '../generic/Button';
import Card from '../generic/Card/Card';
import type { FailedFetchResponse } from '../../helpers/errors';
import type { Organization, SetState } from '../../types';

type CustomContribCountCardProps = {
  org: Organization;
  setOrg: SetState<Organization>;
};

const CustomContribCountCard: VoidFunctionComponent<Omit<CustomContribCountCardProps, 'isOrgLoading'>> = ({
  org,
  setOrg,
}) => {
  const [contributorCount, setContributorCount] = useState(org.custom_contributor_count || org.contributor_count || 0);
  const { status, onError, onPending, onSuccess } = useAsyncStatus();

  useEffect(() => {
    setContributorCount(org.custom_contributor_count || org.contributor_count || 0);
  }, [org]);

  const hasCustomContributors = useMemo(() => {
    return !!org.custom_contributor_count;
  }, [org]);

  const monthlyCost = useMemo(() => {
    const plan = getSubscription({ plan: org.stripe_plan, stripePriceLimit: org.stripePriceLimit });
    return contributorCount * plan.price;
  }, [contributorCount, org]);

  const submitContributorCount = useCallback(async () => {
    onPending();
    try {
      await api.put('/api/organization/custom_contributor_count', { contributorCount });
      setOrg({
        ...org,
        custom_billing: true,
      });
      onSuccess();
    } catch (err) {
      onError(err as FailedFetchResponse);
    }
  }, [contributorCount, onError, onPending, onSuccess, org, setOrg]);

  const deleteContributorCount = useCallback(async () => {
    onPending();
    try {
      await api.remove('/api/organization/custom_contributor_count');
      onSuccess();
      window.location.reload();
    } catch (err) {
      onError(err as FailedFetchResponse);
    }
  }, [onError, onSuccess, onPending]);

  return (
    <div>
      <input
        type="number"
        value={contributorCount}
        onChange={(event) => {
          setContributorCount(Number(event.target.value));
        }}
      />
      <p>They&#39;ll be charged ${monthlyCost}/month</p>
      <ButtonGroup>
        <InlineLoaderButton type="slate" onClick={submitContributorCount} isLoading={status.loading}>
          Submit
        </InlineLoaderButton>
        <InlineLoaderButton
          type="error"
          onClick={deleteContributorCount}
          isDisabled={!hasCustomContributors}
          isLoading={status.loading}
        >
          Remove custom count
        </InlineLoaderButton>
      </ButtonGroup>
    </div>
  );
};

const ContributorCountCard: VoidFunctionComponent<CustomContribCountCardProps> = ({
  org,
  setOrg,
}) => {
  let content: ReactNode;
  if (org.custom_billing) {
    content = <p>This organization has custom billing.</p>;
  } else {
    content = <CustomContribCountCard org={org} setOrg={setOrg} />;
  }

  return (
    <Card isFullWidth padding="medium">
      <Card.Content>
        <Accordion id="lock-contributor-count" isInitiallyOpen={false}>
          <Accordion.Target>Lock contributor count</Accordion.Target>
          <Accordion.Body>
            {content}
          </Accordion.Body>
        </Accordion>
      </Card.Content>
    </Card>
  );
};

export default ContributorCountCard;
